import React, { useState, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Layers, Eye, Underline, Type, RotateCcw } from 'lucide-react';
import { useThemeStore } from '../stores/themeStore';

interface AccessibilityMenuProps {
  onClose: () => void;
}

export default function AccessibilityMenu({ onClose }: AccessibilityMenuProps) {
  const { darkMode } = useThemeStore();
  const [fontSize, setFontSize] = useState(100);
  const [grayScale, setGrayScale] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [negativeContrast, setNegativeContrast] = useState(false);
  const [underlineLinks, setUnderlineLinks] = useState(false);
  const [readableFont, setReadableFont] = useState(false);

  // Apply accessibility settings
  useEffect(() => {
    // Font size
    document.documentElement.style.fontSize = `${fontSize}%`;
    
    // Grayscale
    if (grayScale) {
      document.documentElement.classList.add('grayscale');
    } else {
      document.documentElement.classList.remove('grayscale');
    }
    
    // High contrast
    if (highContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
    
    // Negative contrast
    if (negativeContrast) {
      document.documentElement.classList.add('negative-contrast');
    } else {
      document.documentElement.classList.remove('negative-contrast');
    }
    
    // Underline links
    if (underlineLinks) {
      document.documentElement.classList.add('underline-links');
    } else {
      document.documentElement.classList.remove('underline-links');
    }
    
    // Readable font
    if (readableFont) {
      document.documentElement.classList.add('readable-font');
    } else {
      document.documentElement.classList.remove('readable-font');
    }
    
    // Cleanup function
    return () => {
      // No cleanup needed as we'll keep the settings
    };
  }, [fontSize, grayScale, highContrast, negativeContrast, underlineLinks, readableFont]);

  const resetSettings = () => {
    setFontSize(100);
    setGrayScale(false);
    setHighContrast(false);
    setNegativeContrast(false);
    setUnderlineLinks(false);
    setReadableFont(false);
    
    // Reset all classes and styles
    document.documentElement.style.fontSize = '100%';
    document.documentElement.classList.remove(
      'grayscale', 
      'high-contrast', 
      'negative-contrast', 
      'underline-links', 
      'readable-font'
    );
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 10, 150));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 10, 80));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-md p-6`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Outils d'accessibilité
          </h2>
          <button 
            onClick={onClose}
            className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ZoomIn className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Augmenter le texte</span>
            </div>
            <button 
              onClick={increaseFontSize}
              className={`p-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-lg`}
            >
              <ZoomIn className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ZoomOut className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Diminuer le texte</span>
            </div>
            <button 
              onClick={decreaseFontSize}
              className={`p-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-lg`}
            >
              <ZoomOut className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Layers className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Niveaux de gris</span>
            </div>
            <button 
              onClick={() => setGrayScale(!grayScale)}
              className={`p-2 ${grayScale 
                ? 'bg-blue-600 text-white' 
                : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} 
                rounded-lg`}
            >
              {grayScale ? 'Activé' : 'Désactivé'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Eye className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Haut contraste</span>
            </div>
            <button 
              onClick={() => setHighContrast(!highContrast)}
              className={`p-2 ${highContrast 
                ? 'bg-blue-600 text-white' 
                : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} 
                rounded-lg`}
            >
              {highContrast ? 'Activé' : 'Désactivé'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Eye className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Contraste négatif</span>
            </div>
            <button 
              onClick={() => setNegativeContrast(!negativeContrast)}
              className={`p-2 ${negativeContrast 
                ? 'bg-blue-600 text-white' 
                : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} 
                rounded-lg`}
            >
              {negativeContrast ? 'Activé' : 'Désactivé'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Underline className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Liens soulignés</span>
            </div>
            <button 
              onClick={() => setUnderlineLinks(!underlineLinks)}
              className={`p-2 ${underlineLinks 
                ? 'bg-blue-600 text-white' 
                : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} 
                rounded-lg`}
            >
              {underlineLinks ? 'Activé' : 'Désactivé'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Type className={`w-5 h-5 mr-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Police lisible</span>
            </div>
            <button 
              onClick={() => setReadableFont(!readableFont)}
              className={`p-2 ${readableFont 
                ? 'bg-blue-600 text-white' 
                : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} 
                rounded-lg`}
            >
              {readableFont ? 'Activé' : 'Désactivé'}
            </button>
          </div>

          <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={resetSettings}
              className="flex items-center justify-center w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}